import { EntityKindHandler, type SplitEntities, type EntityMenuContext, type ToolbarAction, type ToolbarActionContext } from '../EntityKindHandler';
import type { CanvasEntity, Rect, SegmentType, StrokeStyle, ArrowMarkerType } from '../../entityTypes';
import { getConnectorData, DEFAULT_EDGE_COLOR, DEFAULT_EDGE_WIDTH, DEFAULT_STROKE_STYLE, DEFAULT_ARROW_START } from '../../entityTypes';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Trash2 } from 'lucide-react';
import { buildConnectorStyleSubmenus, buildConnectorLabelItems, buildMultiDeleteItem } from '../../lib/contextMenuHelpers';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { EDGE_COLORS, STROKE_STYLES, STROKE_WIDTH_OPTIONS, ARROW_TYPES } from '../../lib/edgeStyleConstants';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const SEGMENT_TYPE_OPTIONS = [
  { id: 'bezier', label: t('canvasEntity.segmentBezier') },
  { id: 'straight', label: t('canvasEntity.segmentStraight') },
  { id: 'ortho90', label: t('canvasEntity.segmentL') },
  { id: 'angle45', label: t('canvasEntity.segment45') },
  { id: 'elbowed', label: t('canvasEntity.segment90') },
];

export class ConnectorHandler extends EntityKindHandler {
  readonly kind = 'connector';
  canDrag = false;
  canSnap = false;
  canBoxSelect = false;
  hasOwnRect = false;

  getBounds(_entity: CanvasEntity, _allEntities?: CanvasEntity[]): Rect {
    // Connectors don't have meaningful standalone bounds.
    // The bounding box of the path is computed during rendering.
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const cd = getConnectorData(entity);
    const defaultArrowEnd: ArrowMarkerType = (cd.segmentType === 'bezier' || cd.segmentType === 'straight') ? 'arrow' : 'none';
    return [
      {
        name: 'segmentType',
        label: t('canvasEntity.connectorType'),
        type: 'select',
        options: SEGMENT_TYPE_OPTIONS,
        getValue: () => cd.segmentType,
        apply: (v) => store.send({ type: 'connector.setType', connectorId: entity.id, segmentType: v as SegmentType }),
      },
      {
        name: 'strokeColor',
        label: t('canvasEntity.lineColor'),
        type: 'color',
        options: EDGE_COLORS,
        getValue: () => cd.strokeColor ?? DEFAULT_EDGE_COLOR,
        apply: (v) => store.send({ type: 'connector.updateStyle', connectorId: entity.id, changes: { strokeColor: v as string } }),
      },
      {
        name: 'strokeStyle',
        label: t('canvasEntity.lineStyle'),
        type: 'select',
        options: [...STROKE_STYLES],
        getValue: () => cd.strokeStyle ?? DEFAULT_STROKE_STYLE,
        apply: (v) => store.send({ type: 'connector.updateStyle', connectorId: entity.id, changes: { strokeStyle: v as StrokeStyle } }),
      },
      {
        name: 'strokeWidth',
        label: t('canvasEntity.strokeWidth'),
        type: 'select',
        options: [...STROKE_WIDTH_OPTIONS],
        getValue: () => String(cd.strokeWidth ?? DEFAULT_EDGE_WIDTH),
        apply: (v) => store.send({ type: 'connector.updateStyle', connectorId: entity.id, changes: { strokeWidth: Number(v) } }),
      },
      {
        name: 'arrowStart',
        label: t('canvasEntity.arrowStart'),
        type: 'select',
        options: [...ARROW_TYPES],
        getValue: () => cd.arrowStart ?? DEFAULT_ARROW_START,
        apply: (v) => store.send({ type: 'connector.updateStyle', connectorId: entity.id, changes: { arrowStart: v as ArrowMarkerType } }),
      },
      {
        name: 'arrowEnd',
        label: t('canvasEntity.arrowEnd'),
        type: 'select',
        options: [...ARROW_TYPES],
        getValue: () => cd.arrowEnd ?? defaultArrowEnd,
        apply: (v) => store.send({ type: 'connector.updateStyle', connectorId: entity.id, changes: { arrowEnd: v as ArrowMarkerType } }),
      },
    ];
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity } = ctx;
    const segmentId = entity.id;
    const items: ContextMenuItem[] = [];

    // --- Pos. 2: Style submenus (Linientyp, Farbe, Stil, Staerke, Pfeile) ---
    items.push(...buildConnectorStyleSubmenus(entity, store));

    // --- Label items ---
    items.push(...buildConnectorLabelItems(entity, store));

    // --- Pos. 7: Delete segment ---
    items.push({
      id: 'delete-segment',
      label: t('canvasEntity.deleteConnector'),
      icon: Trash2,
      iconColor: '#dc2626',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: segmentId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });

    // --- Pos. 8: Multi-delete ---
    const multiDelete = buildMultiDeleteItem(ctx, t('canvasEntity.connections'));
    if (multiDelete) {
      items.push(multiDelete);
    }

    return items;
  }

  onRemove(entityId: string, split: SplitEntities): SplitEntities {
    // Simply remove the connector, no additional cleanup needed
    const { [entityId]: _, ...restConnectors } = split.connectors;
    return {
      nodes: split.nodes,
      connectors: restConnectors,
      groups: split.groups,
    };
  }
}
